/**
 * Error logging utility for consistent error tracking
 */

import { AppError } from '../errors';
import { captureSentryException, addSentryBreadcrumb, setSentryContext } from '../lib/sentry';
import { trackPostHogEvent } from '../lib/posthog';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface ErrorLogEntry {
  timestamp: string;
  message: string;
  name: string;
  code?: string;
  status?: number;
  severity: ErrorSeverity;
  userId?: string;
  url?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
  stack?: string;
}

/**
 * Determine error severity based on error type and status
 */
function getSeverity(error: Error, status?: number): ErrorSeverity {
  if (error instanceof AppError) {
    if (error.status >= 500) return 'critical';
    if (error.status >= 400) return 'medium';
    return 'low';
  }

  if (status) {
    if (status >= 500) return 'critical';
    if (status >= 400) return 'medium';
    return 'low';
  }

  // Unknown errors are treated as high severity
  return 'high';
}

/**
 * Create error log entry
 */
function createLogEntry(
  error: Error,
  options: {
    userId?: string;
    metadata?: Record<string, any>;
  } = {}
): ErrorLogEntry {
  const isAppError = error instanceof AppError;
  const severity = getSeverity(error, isAppError ? error.status : undefined);

  return {
    timestamp: new Date().toISOString(),
    message: error.message,
    name: error.name,
    code: isAppError ? error.code : undefined,
    status: isAppError ? error.status : undefined,
    severity,
    userId: options.userId,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof window !== 'undefined' ? navigator.userAgent : undefined,
    metadata: options.metadata,
    stack: error.stack,
  };
}

/**
 * Log error to console (development)
 */
function logToConsole(entry: ErrorLogEntry) {
  const logMethod =
    entry.severity === 'critical'
      ? console.error
      : entry.severity === 'high'
      ? console.warn
      : console.log;

  const logMessage = `[${entry.severity.toUpperCase()}] ${entry.name}: ${entry.message}`;

  if (entry.code) {
    logMethod(logMessage, `(Code: ${entry.code})`);
  } else {
    logMethod(logMessage);
  }

  if (entry.metadata) {
    console.log('Metadata:', entry.metadata);
  }

  if (entry.stack && entry.severity !== 'low') {
    console.log('Stack:', entry.stack);
  }
}

/**
 * Send error to Sentry
 */
function sendToSentry(entry: ErrorLogEntry, error: Error) {
  // Set user context if available
  if (entry.userId) {
    setSentryContext('user', { id: entry.userId });
  }

  // Set additional context
  if (entry.metadata) {
    setSentryContext('error_metadata', entry.metadata);
  }

  // Add breadcrumb
  addSentryBreadcrumb(
    `${entry.name}: ${entry.message}`,
    {
      code: entry.code,
      status: entry.status,
      severity: entry.severity,
      url: entry.url,
    },
    entry.severity === 'critical' || entry.severity === 'high' ? 'error' : 'info'
  );

  // Capture exception
  captureSentryException(error, {
    tags: {
      severity: entry.severity,
      code: entry.code || 'unknown',
      error_type: entry.name,
    },
    extra: {
      ...entry.metadata,
      url: entry.url,
      userAgent: entry.userAgent,
    },
  });
}

/**
 * Track error in PostHog for analytics
 */
function trackInPostHog(entry: ErrorLogEntry) {
  trackPostHogEvent('error_occurred', {
    error_name: entry.name,
    error_code: entry.code,
    error_status: entry.status,
    error_severity: entry.severity,
    error_message: entry.message,
    url: entry.url,
    user_id: entry.userId,
  });
}

/**
 * Error logger options
 */
export interface ErrorLoggerOptions {
  userId?: string;
  metadata?: Record<string, any>;
  sendToSentry?: boolean;
  trackInAnalytics?: boolean;
  logToConsole?: boolean;
}

/**
 * Main error logging function
 * 
 * @param error - Error to log
 * @param options - Logging options
 * 
 * @example
 * ```typescript
 * import { logError } from '@/src/utils/errorLogger';
 * import { ApiError } from '@/src/errors';
 * 
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   logError(error, { userId: user.id, metadata: { operation: 'fetch' } });
 *   throw error;
 * }
 * ```
 */
export function logError(
  error: Error,
  options: ErrorLoggerOptions = {}
): ErrorLogEntry {
  const {
    userId,
    metadata,
    sendToSentry = import.meta.env.PROD,
    trackInAnalytics = import.meta.env.PROD,
    logToConsoleEnabled = import.meta.env.DEV,
  } = options;

  const entry = createLogEntry(error, { userId, metadata });

  // Always log to console in development
  if (logToConsoleEnabled) {
    logToConsole(entry);
  }

  // Send to Sentry in production
  if (sendToSentry) {
    sendToSentry(entry, error);
  }

  // Track in PostHog for analytics
  if (trackInAnalytics) {
    trackInPostHog(entry);
  }

  return entry;
}

/**
 * Create an error logger with preset options
 */
export function createErrorLogger(defaultOptions: ErrorLoggerOptions) {
  return (error: Error, options: ErrorLoggerOptions = {}) =>
    logError(error, { ...defaultOptions, ...options });
}

/**
 * Higher-order function to wrap async operations with error logging
 */
export function withErrorLogging<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: ErrorLoggerOptions & { operationName?: string }
): T {
  const { operationName, ...logOptions } = options;

  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      logError(error as Error, {
        ...logOptions,
        metadata: {
          ...logOptions.metadata,
          operation: operationName || fn.name,
          args: args.slice(0, 3), // Log first 3 args for context
        },
      });
      throw error;
    }
  }) as T;
}
